import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingState } from '@/components/ui/loading-state';
import { ClipboardList, User as UserIcon } from 'lucide-react';
import { apiFetch } from '@/lib/http';

type Assignment = {
  id: number;
  campaignId: number;
  storeId: number;
  packs: number;
  campaignName: string;
  retailerName: string;
  cycleId: number;
  status: string;
  taskId?: number;
  taskName?: string;
  rate?: number;
  earnings?: number;
};

export default function OperatorCycleReports() {
  const [selectedOperator, setSelectedOperator] = useState<number | null>(null);
  
  // Load operators list for dropdown
  const { data: operators = [] } = useQuery({
    queryKey: ['operators'],
    queryFn: () => apiFetch('/api/users/operators').then(r => r.json()),
  });

  // Load assignments for the selected operator - only fetch when operator is selected
  const { data: assignments = [], isLoading: aLoad } = useQuery<Assignment[]>({
    queryKey: ['/api/admin/assignments', selectedOperator],
    queryFn: () =>
      fetch(`/api/admin/assignments?operatorId=${selectedOperator}`, { credentials: 'omit' }).then(r => r.json()),
    enabled: !!selectedOperator, // Only fetch when operator is selected
  });

  // Load storeTaskMap for task ID lookup
  const { data: storeTasks = [] } = useQuery({
    queryKey: ['storeTaskMap'],
    queryFn: () =>
      fetch('/api/campaigns/storeTaskMap', { credentials: 'omit' }).then(r => r.json()),
    staleTime: 300_000, // 5 minutes
  });

  // Build lookup table (campaignId-storeId → taskId)
  const taskLookup = storeTasks.reduce((acc: any, task: any) => {
    acc[`${task.campaignId}-${task.storeId}`] = task.taskId;
    return acc;
  }, {});

  // Remove the global loading check - we'll handle it in the JSX conditionally

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Operator Cycle Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <label htmlFor="operator-select" className="block text-sm font-medium mb-2">
              Select Operator:
            </label>
            <select
              id="operator-select"
              value={selectedOperator ?? ''}
              onChange={e => setSelectedOperator(
                e.target.value ? Number(e.target.value) : null
              )}
              className="border rounded p-2 w-full max-w-xs"
            >
              <option value="">— choose operator —</option>
              {operators.map((operator: any) => (
                <option key={operator.id} value={operator.id}>
                  {operator.name}
                </option>
              ))}
            </select>
          </div>

          {!selectedOperator ? (
            <EmptyState
              icon={UserIcon}
              title="Select an operator"
              description="Choose an operator to view their cycle reports."
              className="py-6"
            />
          ) : aLoad ? (
            <div className="py-6 flex justify-center">
              <LoadingState variant="inline" message="Loading assignments..." />
            </div>
          ) : assignments.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="No assignments"
              description="This operator has no assignments yet."
              className="py-6"
            />
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                Showing {assignments.length} assignments for selected operator
              </p>
              
              <div className="space-y-3">
                {assignments.map((assignment) => {
                  const key = `${assignment.campaignId}-${assignment.storeId}`;
                  const taskId = taskLookup[key];
                  
                  return (
                    <div key={assignment.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium">{assignment.campaignName}</div>
                        <div className="text-sm text-muted-foreground">
                          {assignment.retailerName} • Store ID: {assignment.storeId}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Campaign ID: {assignment.campaignId}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="space-y-1">
                          {taskId ? (
                            <Badge variant="default">Task ID: {taskId}</Badge>
                          ) : (
                            <Badge variant="destructive">No mapping</Badge>
                          )}
                          
                          <div className="text-sm text-muted-foreground">
                            Status: {assignment.status}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}